import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

import {
  getFileMetadata,
  handleHistogramError,
} from "./api/leadTimeHistogramApi";
import { makeRequest } from '../../../services/apiClient';

import { useLocalFilters } from '../../../hooks/useLocalFilters';
import { useAutoLoad } from '../../../hooks/useAutoLoad';
import { formatNumber } from '../../../utils/numberFormat';
import TileSettingsModal from '../../../components/common/TileSettingsModal';
import LeadTimeHistogramSettings from './LeadTimeHistogramSettings';
import LocalFiltersActiveBadge from '../../../components/common/LocalFiltersActiveBadge';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const GRAY_BG = "rgba(156, 163, 175, 0.6)";
const GRAY_BORDER = "rgba(156, 163, 175, 1)";
// #3887D3 — matches ResourceUtilizationChart's comparison-period blue.
// Solid (no alpha), same as that component's own bars — at the 0.6 alpha
// used elsewhere in this file, the hue washes out to a generic pale blue
// indistinguishable from the previous color once blended over white.
const BLUE_BG = "rgba(56, 135, 211, 1)";
const BLUE_BORDER = "rgba(56, 135, 211, 1)";
const ORANGE_BG = "rgba(147, 210, 250, 0.6)";
const ORANGE_BORDER = "rgba(147, 210, 250, 1)";
const PURPLE_BG = "rgba(29, 78, 216, 0.7)";
const PURPLE_BORDER = "rgba(29, 78, 216, 1)";

const LeadTimeHistogram = ({ activeFile, onRefresh, onGetHeaderActions, defaultObjectType, onRegisterSettingsGetter, initialSettings, onTitleChange }) => {
  const { t } = useTranslation();
  // ✅ Use local filters hook - automatically selects local or global filters
  const {
    localFilters,
    updateLocalFilter,
    clearLocalFilters,
    hasLocalFilters,
    localFilterCount,
    api,
    currentFilters,
    startDraftMode,
    applyDraftFilters,
    cancelDraftFilters
  } = useLocalFilters(activeFile, initialSettings?.localFilters ?? null);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [pendingApply, setPendingApply] = useState(false);
  // The fields the first fetch depends on start from the saved config
  // synchronously: the mount-time auto-load runs before the restore effect
  // below has re-rendered, and would otherwise fire one request with the
  // defaults whose response could land after — and overwrite — the correct one.
  const [objectType, setObjectType] = useState(() => initialSettings?.objectType ?? null);
  const [loadedObjectType, setLoadedObjectType] = useState(null); // Track which object type the current data belongs to
  const [mode, setMode] = useState(() => initialSettings?.mode ?? "auto");
  const [manualSettings, setManualSettings] = useState(() => initialSettings?.manualSettings ?? {
    min: "",
    max: "",
    interval: "",
  });

  const [currentDataMode, setCurrentDataMode] = useState("auto");

  const [startEvents, setStartEvents] = useState(() => initialSettings?.startEvents ?? []);
  const [endEvents, setEndEvents] = useState(() => initialSettings?.endEvents ?? []);
  const [activitiesForObjectType, setActivitiesForObjectType] = useState([]);

  const [fileMetadata, setFileMetadata] = useState(null);
  const [availableObjectTypes, setAvailableObjectTypes] = useState([]);

  const [componentTitle, setComponentTitle] = useState('Lead Time Histogram');

  // Settings modal state
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  // Restore settings from saved config (mount-only), then always trigger initial load
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (initialSettings) {
      if (initialSettings.objectType !== undefined) setObjectType(initialSettings.objectType);
      if (initialSettings.mode !== undefined) setMode(initialSettings.mode);
      if (initialSettings.manualSettings !== undefined) setManualSettings(initialSettings.manualSettings);
      if (initialSettings.startEvents !== undefined) setStartEvents(initialSettings.startEvents);
      if (initialSettings.endEvents !== undefined) setEndEvents(initialSettings.endEvents);
      if (initialSettings.componentTitle !== undefined) setComponentTitle(initialSettings.componentTitle);
    }
    setPendingApply(true);
  }, []);

  // Register settings getter with parent so it can be collected on Save
  useEffect(() => {
    onRegisterSettingsGetter?.(() => ({ objectType, mode, manualSettings, startEvents, endEvents, componentTitle, localFilters }));
  }, [objectType, mode, manualSettings, startEvents, endEvents, componentTitle, localFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live-sync the tile title to the parent (GridComponent header) — also fires after
  // the mount-restore effect applies a saved componentTitle, so a renamed tile keeps
  // its custom header text after a reload.
  const onTitleChangeRef = useRef(onTitleChange);
  useEffect(() => { onTitleChangeRef.current = onTitleChange; });
  useEffect(() => {
    onTitleChangeRef.current?.(componentTitle);
  }, [componentTitle]);

  const handleOpenSettings = useCallback(() => {
    startDraftMode(); // Enter draft mode when opening modal
    setIsSettingsModalOpen(true);
  }, [startDraftMode]);

  const handleSettingsOpenChange = useCallback((val) => {
    setIsSettingsModalOpen(val);
    if (!val) cancelDraftFilters();
  }, [cancelDraftFilters]);


  const processHistogramData = (histogramData, currentMode, useDays) => {
    if (!histogramData || !Array.isArray(histogramData)) {
      return null;
    }

    const fmtNum = (n) => formatNumber(n, { decimals: 1, minDecimals: 1 });
    // Drops the "h" unit entirely (in both days and hours mode) since the axis
    // title already states the unit — only the numeric run next to "h" is
    // rewritten, so surrounding data-derived text (e.g. "Bottom 5% (≤ …)",
    // "(12.3%)") is left untouched.
    const convertLabel = (label) =>
      label
        .replace(/([\d.]+)\s*-\s*([\d.]+)\s*h\b/, (_, a, b) =>
          `${fmtNum(useDays ? parseFloat(a) / 24 : parseFloat(a))} - ${fmtNum(useDays ? parseFloat(b) / 24 : parseFloat(b))}`)
        .replace(/([\d.]+)\s*h\b/g, (_, n) =>
          fmtNum(useDays ? parseFloat(n) / 24 : parseFloat(n)));

    const labels = [];

    if (currentMode === "auto") {
      const values = [];
      const backgrounds = [];
      const borders = [];

      const isTailFlags = [];

      histogramData.forEach((item) => {
        labels.push(convertLabel(item.leadTimeRange));
        values.push(item.numberOfObjects);

        const isTail = item.binType === "underflow" || item.binType === "overflow";
        isTailFlags.push(isTail);

        if (item.binType === "underflow") {
          backgrounds.push(ORANGE_BG);
          borders.push(ORANGE_BORDER);
        } else if (item.binType === "overflow") {
          backgrounds.push(PURPLE_BG);
          borders.push(PURPLE_BORDER);
        } else {
          backgrounds.push(BLUE_BG);
          borders.push(BLUE_BORDER);
        }
      });

      return {
        labels,
        datasets: [
          {
            label: "Number of Objects",
            data: values,
            backgroundColor: backgrounds,
            borderColor: borders,
            borderWidth: 1,
            borderRadius: 6,
            barPercentage: 0.7,
            categoryPercentage: 0.8,
          },
        ],
      };
    } else {
      const values = [];
      const backgrounds = [];
      const borders = [];
      const isTailFlags = [];

      histogramData.forEach((item) => {
        labels.push(convertLabel(item.leadTimeRange));
        values.push(item.numberOfObjects);

        const isTail = item.binType === "underflow" || item.binType === "overflow";
        isTailFlags.push(isTail);
        backgrounds.push(isTail ? GRAY_BG : BLUE_BG);
        borders.push(isTail ? GRAY_BORDER : BLUE_BORDER);
      });

      return {
        labels,
        datasets: [
          {
            label: "Number of Objects",
            data: values,
            backgroundColor: backgrounds,
            borderColor: borders,
            borderWidth: 1,
            borderRadius: 6,
            barPercentage: 0.7,
            categoryPercentage: 0.8,
          },
        ],
      };
    }
  };

  // Only the most recently started fetch may commit its result — a slower,
  // older request must not overwrite a newer one.
  const fetchSeqRef = useRef(0);
  const fetchHistogramData = useCallback(async () => {
    if (!activeFile) {
      setError("No active file selected");
      return;
    }

    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);

    try {
      let currentMetadata = fileMetadata;
      let currentObjectType = objectType;

      if (!currentMetadata) {
        console.log(
          "🔄 First time loading: Fetching metadata for:",
          activeFile
        );
        currentMetadata = await getFileMetadata(activeFile);
        setFileMetadata(currentMetadata);
        if (seq !== fetchSeqRef.current) return;

        if (currentMetadata.object_types) {
          const allTypes = Object.keys(currentMetadata.object_types);

          // ✅ Filter object types based on current filters (local or global)
          let filteredTypes = allTypes;
          if (currentFilters?.objectTypes && currentFilters.objectTypes.length > 0) {
            filteredTypes = allTypes.filter(type =>
              currentFilters.objectTypes.includes(type)
            );
            console.log(`🔍 ${hasLocalFilters ? 'Local' : 'Global'} filter applied: ${allTypes.length} types → ${filteredTypes.length} types`);
          }

          setAvailableObjectTypes(filteredTypes);

          // ✅ Apply default object type if provided and valid, otherwise use first available
          if (!currentObjectType || !filteredTypes.includes(currentObjectType)) {
            // Check for default first
            if (defaultObjectType && filteredTypes.includes(defaultObjectType)) {
              currentObjectType = defaultObjectType;
              setObjectType(currentObjectType);
              console.log(`✅ Object type set to default: '${currentObjectType}'`);
            } else {
              // Fall back to first available
              currentObjectType = filteredTypes[0];
              setObjectType(currentObjectType);
              console.log(`✅ Object type set to '${currentObjectType}' (first available type)`);
            }
          }
        } else {
          console.warn("No object types found in metadata");
          setAvailableObjectTypes([]);
          setLoading(false);
          setError("No object types available for this dataset");
          return;
        }
      }

      // Ensure we have a valid object type before making the request
      if (!currentObjectType) {
        console.error("❌ Cannot fetch histogram: objectType is null");
        setLoading(false);
        setError("No object type selected. Please select an object type.");
        return;
      }

      console.log("🔄 Fetching histogram data...", {
        activeFile,
        objectType: currentObjectType,
        mode,
        filters: currentFilters,
        usingLocalFilters: hasLocalFilters
      });

      // ✅ Filters are automatically merged by api (local or global)
      const componentParams = {
        object_type: currentObjectType,
        mode: mode,
        ...(mode === "manual" && {
          manual_min: parseFloat(manualSettings.min),
          manual_max: parseFloat(manualSettings.max),
          manual_interval: parseFloat(manualSettings.interval),
        }),
      };

      const useCustomEvents = startEvents.length > 0 && endEvents.length > 0;
      const endpoint = useCustomEvents ? '/custom-lead-time-histogram' : '/filtered-lead-time-histogram';
      const requestParams = useCustomEvents
        ? { ...componentParams, start_events: startEvents, end_events: endEvents }
        : componentParams;

      console.log("📤 Sending histogram request:", { endpoint, ...requestParams });
      const response = await api.post(endpoint, requestParams, {}, 'Lead Time Histogram');
      if (seq !== fetchSeqRef.current) return;
      console.log("📥 Histogram response received:", response);

      if (response && response[currentObjectType]) {
        setData(response[currentObjectType]);
        setLoadedObjectType(currentObjectType); // Track which object type this data belongs to
        setCurrentDataMode(mode);
      } else {
        setData(null);
        setError("No data available for the selected configuration");
      }
      setHasFetched(true);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      console.error("❌ Error during data fetch:", err);
      const errorInfo = handleHistogramError(err);
      setError(errorInfo.message + ". " + errorInfo.suggestedAction);
      setHasFetched(true);
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, [activeFile, objectType, mode, manualSettings, api, fileMetadata, hasLocalFilters, currentFilters, startEvents, endEvents]);

  // ✅ Auto-reload when filters change (local or global)
  useEffect(() => {
    if (hasFetched && activeFile && !loading) {
      fetchHistogramData();
    }
  }, [currentFilters, activeFile]);

  // ✅ Re-filter available object types when filters change (local or global)
  useEffect(() => {
    if (fileMetadata?.object_types) {
      const allTypes = Object.keys(fileMetadata.object_types);

      let filteredTypes = allTypes;
      if (currentFilters?.objectTypes && currentFilters.objectTypes.length > 0) {
        filteredTypes = allTypes.filter(type =>
          currentFilters.objectTypes.includes(type)
        );
        console.log(`🔍 Object types filtered by ${hasLocalFilters ? 'local' : 'global'} filter: ${allTypes.length} → ${filteredTypes.length}`);
      }

      setAvailableObjectTypes(filteredTypes);

      // ✅ Apply default object type if provided and valid
      if (defaultObjectType && filteredTypes.includes(defaultObjectType) && !objectType) {
        setObjectType(defaultObjectType);
        console.log(`✅ Applied default object type: '${defaultObjectType}'`);
      }
      // If current object type is no longer in filtered list, reset to first available
      else if (objectType && !filteredTypes.includes(objectType)) {
        if (filteredTypes.length > 0) {
          setObjectType(filteredTypes[0]);
          console.log(`🔄 Object type reset to '${filteredTypes[0]}' due to filter change`);
        } else {
          setObjectType(null);
          console.warn("⚠️ No object types available after filter");
        }
      }
    }
  }, [currentFilters?.objectTypes, fileMetadata, objectType]);

  // Fetch activities for the selected object type and filter stale event selections
  useEffect(() => {
    setActivitiesForObjectType([]);
    if (!objectType || !activeFile) return;
    let cancelled = false;
    makeRequest('/object-type-activities', { params: { file_type: activeFile } }, 'Object Type Activities')
      .then((res) => {
        if (cancelled) return;
        const acts = res?.objectTypeActivities?.[objectType] || [];
        setActivitiesForObjectType(acts);
        // Remove any events that are no longer valid for this object type, but
        // preserve restored/user-selected events that are still valid.
        setStartEvents(prev => prev.filter(e => acts.includes(e)));
        setEndEvents(prev => prev.filter(e => acts.includes(e)));
      })
      .catch(() => {
        if (!cancelled) setActivitiesForObjectType([]);
      });
    return () => { cancelled = true; };
  }, [objectType, activeFile]);

  const handleManualSettingsChange = (field, value) => {
    setManualSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const isManualSettingsValid = () => {
    if (mode !== "manual") return true;
    const { min, max, interval } = manualSettings;
    return (
      min !== "" &&
      max !== "" &&
      interval !== "" &&
      parseFloat(min) < parseFloat(max) &&
      parseFloat(interval) > 0
    );
  };

  const useDays = (() => {
    if (!data?.histogramData) return false;
    const nums = data.histogramData.flatMap(item => {
      const ms = [...item.leadTimeRange.matchAll(/(\d+\.?\d*)\s*h\b/g)];
      return ms.map(m => parseFloat(m[1]));
    });
    return nums.length > 0 && Math.max(...nums) >= 48;
  })();

  const chartData = data?.histogramData
    ? processHistogramData(data.histogramData, currentDataMode, useDays)
    : null;

  const hasData = hasFetched && !!data;

  // Handle apply settings - defined after fetchHistogramData
  // pendingApply triggers a fetch via useEffect AFTER state updates land,
  // so fetchHistogramData always runs with the latest mode/filters/settings.
  const handleApplySettings = useCallback(() => {
    applyDraftFilters();
    setPendingApply(true);
    setIsSettingsModalOpen(false);
  }, [applyDraftFilters]);

  useEffect(() => {
    if (!pendingApply) return;
    setPendingApply(false);
    fetchHistogramData();
  }, [pendingApply, fetchHistogramData]);

  // Header actions - settings button and local filters badge
  const headerActions = React.useMemo(() => (
    <div className="flex items-center gap-2">
      {hasLocalFilters && (
        <LocalFiltersActiveBadge
          hasLocalFilters={hasLocalFilters}
          localFilterCount={localFilterCount}
          onClearLocalFilters={clearLocalFilters}
        />
      )}
      <button
        onClick={handleOpenSettings}
        className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label={t('common:actions.openSettings')}
      >
        <svg
          className="w-3 h-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        <span className="text-xs">{t('common:actions.settings')}</span>
      </button>
    </div>
  ), [handleOpenSettings, hasLocalFilters, localFilterCount, clearLocalFilters]);

  // Auto-load data when persistent cache is enabled
  useAutoLoad(fetchHistogramData, [activeFile, currentFilters], hasData);

  // Provide refresh function to parent (GridComponent)
  React.useEffect(() => {
    if (onRefresh) {
      onRefresh(fetchHistogramData);
    }
  }, [onRefresh, fetchHistogramData]);

  // Provide header actions to parent (GridComponent)
  React.useEffect(() => {
    if (onGetHeaderActions) {
      onGetHeaderActions(headerActions);
    }
  }, [onGetHeaderActions, headerActions]);

  // Rendered as a custom row below the chart (same line as the x-axis label,
  // per request) instead of Chart.js's own legend plugin — see chartOptions
  // below, where the legend plugin is disabled.
  const xAxisLabel = useDays
    ? t('tiles:leadTimeHistogram.xAxisDays')
    : t('tiles:leadTimeHistogram.xAxisHours');

  const legendItems = React.useMemo(() => {
    if (currentDataMode === "manual") {
      return [
        { text: t('tiles:leadTimeHistogram.legend.overflowUnderflow'), bg: GRAY_BG, border: GRAY_BORDER },
        { text: t('tiles:leadTimeHistogram.legend.regularBins'), bg: BLUE_BG, border: BLUE_BORDER },
      ];
    }
    if (currentDataMode === "auto") {
      return [
        { text: t('tiles:leadTimeHistogram.legend.bottom5'), bg: ORANGE_BG, border: ORANGE_BORDER },
        { text: t('tiles:leadTimeHistogram.legend.regularBins'), bg: BLUE_BG, border: BLUE_BORDER },
        { text: t('tiles:leadTimeHistogram.legend.top5'), bg: PURPLE_BG, border: PURPLE_BORDER },
      ];
    }
    return [];
  }, [currentDataMode, t]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      subtitle: {
        display: currentDataMode === "auto",
        text:
          currentDataMode === "auto"
            ? "Using Freedman–Diaconis (FD) rule for automatic binning"
            : "",
        padding: {
          bottom: 10,
        },
      },
      tooltip: {
        callbacks: {
          title: function (context) {
            return context[0].label;
          },
          label: function (context) {
            if (context.raw === null) return "";
            const item = data.histogramData[context.dataIndex];
            if (!item) return "";

            const percentage =
              item.percentage !== undefined
                ? item.percentage.toFixed(1)
                : (
                    (item.numberOfObjects / (data.matchedObjectCount ?? data.totalObjectCount)) *
                    100
                  ).toFixed(1);

            if (currentDataMode === "auto") {
              if (item.binType === "underflow") {
                return `Bottom 5%: ${item.numberOfObjects.toLocaleString()} objects (${percentage}%)`;
              } else if (item.binType === "overflow") {
                return `Top 5%: ${item.numberOfObjects.toLocaleString()} objects (${percentage}%)`;
              } else {
                return `Regular: ${item.numberOfObjects.toLocaleString()} objects (${percentage}%)`;
              }
            } else {
              if (item.binType === "underflow") {
                return `Underflow: ${item.numberOfObjects.toLocaleString()} objects (${percentage}%)`;
              } else if (item.binType === "overflow") {
                return `Overflow: ${item.numberOfObjects.toLocaleString()} objects (${percentage}%)`;
              } else {
                return `Filtered: ${item.numberOfObjects.toLocaleString()} objects (${percentage}%)`;
              }
            }
          },
        },
      },
    },
    scales: {
      x: {
        // Title is rendered in the custom row below the chart instead (same
        // line as the legend, per request).
        title: {
          display: false,
        },
        ticks: {
          // Always horizontal, regardless of bin count, so sibling histogram
          // tiles never render their ticks at different angles. Chart.js's
          // default autoSkip hides whichever labels don't fit.
          maxRotation: 0,
          minRotation: 0,
          color: "#6b7280",
          font: { size: 11 },
        },
      },
      y: {
        title: {
          display: true,
          text: t('tiles:leadTimeHistogram.yAxis'),
          color: "#4b5563",
          font: { size: 12 },
        },
        ticks: { color: "#6b7280", font: { size: 11 } },
        beginAtZero: true,
      },
    },
  };

  const renderIntervalAdjustment = () => {
    if (!data?.intervalAdjusted) return null;

    return (
      <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-start">
          <svg
            className="w-5 h-5 text-amber-600 mt-0.5 mr-2 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-amber-800">
              Interval Adjusted
            </h4>
            <p className="text-sm text-amber-700 mt-1">
              {data.adjustmentMessage} Original: {data.originalInterval},
              Adjusted: {data.adjustedInterval}
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col p-6" style={{ background: 'transparent' }}>
      {/* Interval Adjustment Notification */}
      {renderIntervalAdjustment()}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading histogram data...</p>
          </div>
        </div>
      ) : !hasFetched ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
              <svg
                className="w-8 h-8 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Ready to Generate Histogram
            </h3>
            <p className="text-gray-500 mb-4">
              Configure your settings and click "Load Chart" to begin.
            </p>
          </div>
        </div>
      ) : error ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-red-500 mb-4">
              <svg
                className="w-16 h-16 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <p className="text-red-600 font-medium mb-2">Error Loading Data</p>
            <p className="text-gray-600 text-sm mb-4">{error}</p>
            <button
              onClick={fetchHistogramData}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      ) : chartData ? (
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 relative min-h-0">
            <Bar data={chartData} options={chartOptions} />
          </div>
          <div className="shrink-0 grid grid-cols-[1fr_auto_1fr] items-center gap-x-4 gap-y-1 pt-2 text-xs text-gray-600">
            <span aria-hidden="true" />
            <span className="font-medium text-gray-700 text-center whitespace-nowrap">{xAxisLabel}</span>
            <div className="flex items-center justify-end flex-wrap gap-x-4 gap-y-1 min-w-0 pr-3">
              {legendItems.map((item) => (
                <span key={item.text} className="inline-flex items-center gap-1.5 whitespace-nowrap">
                  <span
                    className="inline-block w-3.5 h-3.5 rounded-sm border"
                    style={{ backgroundColor: item.bg, borderColor: item.border }}
                  />
                  {item.text}
                </span>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">
            No data available for this configuration. Try adjusting the settings
            or filters.
          </p>
        </div>
      )}

      {/* Settings Modal */}
      <TileSettingsModal
        open={isSettingsModalOpen}
        onOpenChange={handleSettingsOpenChange}
        onApply={handleApplySettings}
        title="Lead Time Histogram Settings"
        isApplyDisabled={!activeFile || !isManualSettingsValid() || loading}
        applyButtonText={loading ? "Loading..." : hasFetched ? "Update Chart" : "Load Chart"}
      >
        <LeadTimeHistogramSettings
          objectType={objectType}
          onObjectTypeChange={setObjectType}
          availableObjectTypes={availableObjectTypes}
          binningMode={mode}
          onBinningModeChange={setMode}
          manualMin={manualSettings.min}
          manualMax={manualSettings.max}
          manualInterval={manualSettings.interval}
          onManualMinChange={(value) => handleManualSettingsChange("min", value)}
          onManualMaxChange={(value) => handleManualSettingsChange("max", value)}
          onManualIntervalChange={(value) => handleManualSettingsChange("interval", value)}
          fileMetadata={fileMetadata}
          loading={loading}
          localFilters={localFilters}
          updateLocalFilter={updateLocalFilter}
          activeFile={activeFile}
          startEvents={startEvents}
          onStartEventsChange={setStartEvents}
          endEvents={endEvents}
          onEndEventsChange={setEndEvents}
          activitiesForObjectType={activitiesForObjectType}
          componentTitle={componentTitle}
          onTitleChange={setComponentTitle}
        />
      </TileSettingsModal>
    </div>
  );
};

export default LeadTimeHistogram;
